import { useQuery } from "@tanstack/react-query";
import { DocumentService } from "../../../services/document/document.service";
import { getDiffDay, getUCTDate } from "../../../helper/dateTimeHelper";
import { useSearchParams } from "../../../hook/use-search-params";
import { useMemo } from "react";
import { documentStatus } from "../../../constants";

const isDocumentExpired = (effectiveTo: string) => {
  const today = new Date();
  return getDiffDay(effectiveTo, today.toDateString()) >= 0;
};

const anyElementMatches = (arr1: string[], arr2: string[]): boolean => {
  return arr1.some((item1) => arr2.includes(item1));
};

export const useDocumentsInfo = () => {
  const { searchParams } = useSearchParams();
  const start = searchParams.get("start") ?? "";
  const end = searchParams.get("end") ?? "";
  const tags = searchParams.get("tags");

  const { data, isLoading } = useQuery({
    queryKey: ["documents-info"],
    queryFn: () => DocumentService.getDocumentInfoFromWorkingDate(getUCTDate(start), getUCTDate(end)),
    select: (data) => data.data,
  });

  //BH BN NB DST

  const countPublishedDocument = useMemo(
    () => data?.filter((e: any) => e.docStatusValue === documentStatus.publish.value).length,
    [data],
  );

  const composingDocuments = useMemo(
    () => data?.filter((e: any) => e.docStatusValue === documentStatus.compose.value),
    [data],
  );

  const countComposingDocument = useMemo(() => composingDocuments?.length, [composingDocuments?.length]);

  const internalDocument = useMemo(() => data?.filter((e: any) => e.docTypeId !== "2"), [data]);

  const countInternalDocument = useMemo(() => internalDocument?.length, [internalDocument?.length]);

  const externalDocument = useMemo(() => data?.filter((e: any) => e.docTypeId === "2"), [data]);

  const countExternalDocument = useMemo(() => externalDocument?.length, [externalDocument?.length]);

  const validInternalDocuments = useMemo(
    () => internalDocument?.filter((e: any) => !isDocumentExpired(e.effectiveTo)),
    [internalDocument],
  );

  const countValidInternalDocument = useMemo(() => validInternalDocuments?.length, [validInternalDocuments?.length]);

  const expiredInternalDocuments = useMemo(
    () => internalDocument?.filter((e: any) => isDocumentExpired(e.effectiveTo)),
    [internalDocument],
  );

  const countExpiredInternalDocument = useMemo(
    () => expiredInternalDocuments?.length,
    [expiredInternalDocuments?.length],
  );

  const validExternalDocuments = useMemo(
    () => externalDocument?.filter((e: any) => !isDocumentExpired(e.effectiveTo)),
    [externalDocument],
  );

  const countValidExternalDocument = useMemo(() => validExternalDocuments?.length, [validExternalDocuments?.length]);

  const expiredExternalDocuments = useMemo(
    () => externalDocument?.filter((e: any) => isDocumentExpired(e.effectiveTo)),
    [externalDocument],
  );

  const countExpiredExternalDocument = useMemo(
    () => expiredExternalDocuments?.length,
    [expiredExternalDocuments?.length],
  );

  const documentDataHasTags = useMemo(() => {
    const docTags = tags?.split(",");
    if (!docTags) return data?.filter((e: any) => e.Tags?.length > 0);
    return data?.filter((e: any) => e.Tags?.length > 0 && anyElementMatches(e.Tags, docTags));
  }, [data, tags]);

  return {
    countPublishedDocument,
    composingDocuments,
    countComposingDocument,
    internalDocument,
    countInternalDocument,
    externalDocument,
    countExternalDocument,
    validInternalDocuments,
    countValidInternalDocument,
    expiredInternalDocuments,
    countExpiredInternalDocument,
    validExternalDocuments,
    countValidExternalDocument,
    expiredExternalDocuments,
    countExpiredExternalDocument,
    data,
    isLoading,
    documentDataHasTags,
  };
};
